import type {
  NotificationChannel,
  NotificationPreferencesResponse,
  NotificationType,
  UnreadNotifications,
  UserNotificationPreference,
} from '~/interfaces/notifications/main';
import type { JsonApiResponse } from '~/interfaces/api/main';

export const useNotificationsStore = defineStore('notifications', () => {
  const userNotificationPreferences = ref<UserNotificationPreference[]>([]);
  const notificationChannels = ref<NotificationChannel[]>([]);
  const notificationTypes = ref<NotificationType[]>([]);
  const notifications = ref<UnreadNotifications | null>(null);
  const unreadOnly = ref<boolean>(false);
  const loading = ref<boolean>(false);

  async function getNotifications (unreadOnly = false, limit: number | null = null, page: number | null = null): Promise<JsonApiResponse | null> {
    const params: any = {
      sortField: 'createdAt',
      sortOrder: 0,
      limit,
      page,
    };
    if (unreadOnly) {
      params['filters[readAt][null][]'] = true;
    }
    const response = await useAuthFetchNext('notifications', { params });

    if (isJsonApiResponse(response)) {
      return response;
    }
    return null;
  }

  async function fetchUserNotificationPreferences (): Promise<void> {
    loading.value = true;
    try {
      const response = await useAuthFetchNext('notification-preferences');

      if (isJsonApiResponse(response)) {
        const responseData = response.data as NotificationPreferencesResponse;
        userNotificationPreferences.value = responseData.userNotificationPreferences;
        notificationChannels.value = responseData.notificationChannels;
        notificationTypes.value = responseData.notificationTypes;
      }
    } catch (error: any) {
      console.error('An unexpected error occurred in fetchUserNotificationPreferences:', error);
    } finally {
      loading.value = false;
    }
  }

  async function saveUserNotificationPreferences (userNotificationPreferences: UserNotificationPreference[]): Promise<void> {
    const data = { userNotificationPreferences };
    loading.value = true;
    try {
      const result = await useAuthFetchNext('notification-preferences', {
        method: 'POST',
        body: data,
      });

      if (isAPIErrorResponse(result)) {
        useToastService().add({
          severity: 'error',
          summary: 'Préférences de notification',
          detail: result.errors[0]?.title ?? 'Vos préférences de notification n\'ont pas pu être enregistrées.',
          life: 3000,
        });
      } else {
        useToastService().add({
          severity: 'success',
          summary: 'Préférences de notification',
          detail: 'Vos préférences ont été enregistrées avec succès.',
          life: 3000,
        });
      }
    } catch (error: any) {
      useToastService().add({
        severity: 'error',
        summary: 'Préférences de notification',
        detail: 'Une erreur inattendue est survenue lors de l\'enregistrement de vos préférences.',
        life: 3000,
      });
      console.error('An unexpected error occurred in saveUserNotificationPreferences:', error);
    } finally {
      loading.value = false;
    }
  }

  return {
    userNotificationPreferences,
    notificationChannels,
    notificationTypes,
    notifications,
    unreadOnly,
    loading,
    getNotifications,
    fetchUserNotificationPreferences,
    saveUserNotificationPreferences,
  };
});
